import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChooseListInput } from '@/shared/components/inputs/ChooseList.input';
import { Button } from '@/shared/components/ui/Button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/Card';
import { WizardStepProps } from '../types/wizard.types';
import { ComponentReturnTypeMap } from '@momentum/types';
import { Textarea } from '@/shared/components/ui/Textarea';

interface ChoiceOption {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface WizardTextAreaChoiceStepProps
  extends WizardStepProps<ComponentReturnTypeMap['TextAreaChoice']> {
  options: ChoiceOption[];
  columns?: 1 | 2 | 3;
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  textAreaRequired?: boolean;
  choiceRequired?: boolean;
  textAreaLabel?: string;
  choiceLabel?: string;
}

export const WizardTextAreaChoiceStep: React.FC<
  WizardTextAreaChoiceStepProps
> = ({
  value,
  onChange,
  onNext,
  onPrevious: _onPrevious,
  error,
  disabled,
  options,
  choiceLabel,
  columns = 2,
  placeholder = '',
  maxLength = 500,
  minLength = 0,
  textAreaRequired = false,
  choiceRequired = false,
  textAreaLabel = 'Additional Details',
  setSubmitHandler,
}) => {
  const { t } = useTranslation('wizard');
  const { t: tCommon } = useTranslation('common');
  choiceLabel = choiceLabel ?? tCommon('chooseOption');
  // Parse the value to separate text area and choice values
  const parseValue = (val: unknown) => {
    if (
      typeof val === 'object' &&
      val !== null &&
      'textArea' in val &&
      'choice' in val
    ) {
      return {
        textArea: (val as { textArea: string }).textArea || '',
        choice: (val as { choice: string }).choice || '',
      };
    }
    return {
      textArea: '',
      choice: (val as string) || '',
    };
  };

  const [localValue, setLocalValue] = useState(parseValue(value));

  // Update local state when external value changes (e.g., when navigating back)
  useEffect(() => {
    setLocalValue(parseValue(value));
  }, [value]);

  const handleTextAreaChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const newValue = event.target.value;
    setLocalValue((prev) => ({ ...prev, textArea: newValue }));
    const combinedValue = {
      textArea: newValue,
      choice: localValue.choice,
    };
    onChange(combinedValue);
  };

  const handleChoiceChange = (selectedId: string | string[]) => {
    const choiceValue = Array.isArray(selectedId)
      ? selectedId[0] || ''
      : selectedId;
    setLocalValue((prev) => ({ ...prev, choice: choiceValue }));
  };

  const handleNext = () => {
    // Combine both values into a single object
    const combinedValue = {
      textArea: localValue.textArea,
      choice: localValue.choice,
    };

    onChange(combinedValue);
    if (onNext) {
      onNext(combinedValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      handleNext();
    }
  };

  const isTextAreaValid =
    !textAreaRequired || localValue.textArea.trim().length > 0;
  const isChoiceValid = !choiceRequired || localValue.choice.length > 0;

  // If neither is required, require at least one to be filled
  const hasAtLeastOneValue =
    localValue.textArea.trim().length > 0 || localValue.choice.length > 0;
  const isFormValid =
    isTextAreaValid &&
    isChoiceValid &&
    (!textAreaRequired && !choiceRequired ? hasAtLeastOneValue : true);

  // Register submit handler for the card footer's Next button
  const typedSetSubmitHandler = setSubmitHandler as
    | ((handler: (() => void) | null) => void)
    | undefined;
  typedSetSubmitHandler?.(handleNext);

  return (
    <div className="w-full mx-auto space-y-6">
      <div className="space-y-6">
        {/* Text Area Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{textAreaLabel}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Textarea
                value={localValue.textArea}
                onChange={handleTextAreaChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                required={textAreaRequired}
                maxLength={maxLength}
                minLength={minLength}
                rows={4}
                className="resize-none"
              />
              {maxLength && (
                <div className="text-sm text-muted-foreground text-right">
                  {localValue.textArea.length} / {maxLength}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Choice List Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{choiceLabel}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChooseListInput
              options={options}
              value={localValue.choice}
              onChange={handleChoiceChange}
              columns={columns}
              required={choiceRequired}
              disabled={disabled}
              className="w-full"
            />
          </CardContent>
        </Card>
      </div>

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {error}
        </div>
      )}

      {!typedSetSubmitHandler && (
        <div className="flex justify-end">
          <Button onClick={handleNext} disabled={disabled || !isFormValid}>
            {t('common.buttons.next')}
          </Button>
        </div>
      )}
    </div>
  );
};

/* 
This component combines text area and choice list functionality:
- Allows users to select from predefined options AND provide additional text input
- Supports separate validation for both text area and choice selection
- Maintains both values in a combined object structure
- Useful for questions that need both structured and free-form responses
*/
